import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Club, Member, Visibility } from "@pagemates/shared";
import { api, describeError } from "../../lib/api";
import type { PickPolicy } from "@pagemates/shared";
import { Avatar, Confirm, Spinner } from "../../components/ui";
import { PencilIcon } from "../../components/Icons";
import { useClub } from "./ClubLayout";
import { BackBar } from "../../components/ui";
import { GenrePicker } from "../../components/ClubHome";
import { MuteClub } from "../../components/NotificationSettings";

/** One group of settings: its title and a line on what it covers, then its card. Desktop puts the title in a left column. */
export function Group({ title, about, children }: { title: string; about: string; children: React.ReactNode }) {
  return (
    <section className="sgroup">
      <header><h2>{title}</h2><p>{about}</p></header>
      <div className="card stack">{children}</div>
    </section>
  );
}

/** Admin settings, off the Club tab behind the gear. Members see only "Your membership". */
export function ClubSettingsPage() {
  const { clubId, club } = useClub();
  const c = club.data!;
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const members = useQuery({ queryKey: ["members", clubId], queryFn: () => api.members(clubId) });
  const isAdmin = c.myRole !== "member";
  return (
    <main className="page stack settings">
      <BackBar label="Club" link={{ to: "/clubs/$clubId/club", params: { clubId } }} />
      <h1>Settings</h1>
      <div className="sgroups">
        {isAdmin && <Settings club={c} />}
        {members.isPending ? <Spinner /> : <Membership club={c} members={members.data ?? []} meId={me.data?.id} />}
      </div>
    </main>
  );
}

function Settings({ club }: { club: Club }) {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (patch: Parameters<typeof api.updateClub>[1]) => api.updateClub(club.id, patch),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["club"] }), qc.invalidateQueries({ queryKey: ["me"] })]),
  });
  // Everything saves as you change it; whatever just saved says so beside its name for a moment.
  const [saved, setSaved] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const put = (key: string, patch: Parameters<typeof api.updateClub>[1]) => save.mutate(patch, {
    onSuccess: () => { setSaved(key); clearTimeout(timer.current); timer.current = setTimeout(() => setSaved(null), 1800); },
  });
  useEffect(() => () => clearTimeout(timer.current), []);
  const tick = (key: string) => <Saved on={saved === key} />;

  const s = club.settings;
  const books = useQuery({ queryKey: ["books", club.id], queryFn: () => api.clubBooks(club.id) });

  return (
    <>
      <Group title="The club" about="What people see when they find your club or open an invite.">
        <div className="stack">
          <EditField id="s-name" label="Club name" value={club.name} max={60} required saved={tick("name")}
            onSave={(v) => put("name", { name: v })} />
          <EditField id="s-desc" label="One line about the club" value={club.description} max={300} placeholder="Two readers, one lamp." saved={tick("description")}
            onSave={(v) => put("description", { description: v })} />
          <GenrePicker value={club.genres} onSave={(v) => put("genres", { genres: v })} finished={books.data?.finished ?? []} saved={tick("genres")} />
          <EditField id="s-rules" label="How this club works" value={club.rules} max={2000} multiline saved={tick("rules")}
            placeholder="How often you meet, whether unfinished readers come, what you tend to read." hint="Shown on the Club tab and on the invite page."
            onSave={(v) => put("rules", { rules: v })} />
        </div>
        <hr className="divider" />
        {/* The link saves on its own: changing it breaks the old one. */}
        <SlugField club={club} onSave={(v) => put("slug", { slug: v })} saved={tick("slug")} />
      </Group>

      <Group title="Who can find this club" about="Who can see the club and how people join. Only the owner can change this.">
        <VisibilitySetting club={club} saved={tick("visibility")} onSaved={() => { setSaved("visibility"); clearTimeout(timer.current); timer.current = setTimeout(() => setSaved(null), 1800); }} />
      </Group>

      <Group title="Reading together" about="How the club shares the book when everyone reads at their own speed.">
        <div className="switch">
          <div><div className="k">Usual pace{tick("usualPace")}</div><div className="d">Roughly how many pages a day the club reads. People see it before they join, and it helps plan how far each meeting gets.</div></div>
          <NumberSetting id="s-pace" label="Usual pace" value={s.usualPace} min={1} max={500} unit="pages a day" clearable onSave={(v) => put("usualPace", { settings: { usualPace: v } })} />
        </div>
        <hr className="divider" />
        <div className="switch">
          <div><div className="k">Hide spoilers in Talk{tick("spoilerGating")}</div><div className="d">Every comment is marked with how far its writer had read. You only see the ones from up to where you are, and the rest appear as you catch up. When off, everyone sees everything.</div></div>
          <input id="s-gating" className="toggle" type="checkbox" checked={s.spoilerGating} aria-label="Hide spoilers in Talk"
            onChange={(e) => put("spoilerGating", { settings: { spoilerGating: e.target.checked } })} />
        </div>
        <hr className="divider" />
        <div className="switch">
          <div><div className="k">Askers can read answers early{tick("earlyReveal")}</div><div className="d">If someone answers your question from further on in the book, you can choose to read it now. When off, it waits until you reach that part. Nothing else opens early.</div></div>
          <input id="s-reveal" className="toggle" type="checkbox" checked={s.earlyReveal} aria-label="Askers can read answers early"
            onChange={(e) => put("earlyReveal", { settings: { earlyReveal: e.target.checked } })} />
        </div>
      </Group>

      <Group title="Picking the next book" about="How the club decides what to read next.">
        <div className="switch stacked">
          <div><div className="k">How it gets picked{tick("pickPolicy")}</div><div className="d">Whichever you choose, everyone can still suggest books.</div></div>
          <div className="seg" role="group" aria-label="How the next book gets picked">
            {([["admins", "Admins pick"], ["vote", "Vote"], ["turns", "Take turns"]] as [PickPolicy, string][]).map(([k, label]) => (
              <button key={k} type="button" aria-pressed={s.pickPolicy === k} onClick={() => put("pickPolicy", { settings: { pickPolicy: k } })}>{label}</button>
            ))}
          </div>
          <div className="d">{{ admins: "An admin chooses the next book from the shortlist.", vote: "Members vote on the shortlist. An admin opens and closes the vote from the Shelf.", turns: "Members take turns choosing, in the order they joined. Whoever's turn it is starts the next book." }[s.pickPolicy]}</div>
        </div>
        {s.pickPolicy === "vote" && (
          <div className="sub-settings">
            <div className="switch">
              <div><div className="k">Counted by{tick("voteMethod")}</div><div className="d">Ranked: everyone puts the books in order, and first choices count most. Approval: everyone ticks every book they'd happily read, which is quicker in a big club.</div></div>
              <div className="seg" role="group" aria-label="How a vote is counted">
                <button type="button" aria-pressed={s.voteMethod === "ranked"} onClick={() => put("voteMethod", { settings: { voteMethod: "ranked" } })}>Ranked</button>
                <button type="button" aria-pressed={s.voteMethod === "approval"} onClick={() => put("voteMethod", { settings: { voteMethod: "approval" } })}>Approval</button>
              </div>
            </div>
          </div>
        )}
        {s.pickPolicy === "turns" && <div className="sub-settings"><TurnOrder clubId={club.id} /></div>}
        {s.pickPolicy === "turns" && club.visibility === "open" && club.memberCount > 20 && (
          <p className="small notice" style={{ margin: 0 }}>With {club.memberCount} members taking turns, it'll be a long while before everyone gets a pick. A vote may suit an open club this size.</p>
        )}
        <hr className="divider" />
        <div className="switch">
          <div><div className="k">The shortlist is made of{tick("deckFill")}</div><div className="d">The shortlist, called On deck on the Shelf, is what the club picks the next book from. {s.deckFill === "wanted" ? "Right now it's the suggestions the most members want to read; admins can pin others in." : "Right now admins choose it by pinning suggestions."}</div></div>
          <div className="seg" role="group" aria-label="The shortlist is made of">
            <button type="button" aria-pressed={s.deckFill === "wanted"} onClick={() => put("deckFill", { settings: { deckFill: "wanted" } })}>Most wanted</button>
            <button type="button" aria-pressed={s.deckFill === "admins"} onClick={() => put("deckFill", { settings: { deckFill: "admins" } })}>Admins</button>
          </div>
        </div>
        {s.deckFill === "wanted" && (
          <div className="sub-settings">
            <div className="switch">
              <div><div className="k">Shortlist size{tick("deckSize")}</div><div className="d">How many books are on it at once.</div></div>
              <NumberSetting id="s-deck" label="Shortlist size" value={s.deckSize} min={2} max={12} unit="books" onSave={(v) => put("deckSize", { settings: { deckSize: v ?? 6 } })} />
            </div>
          </div>
        )}
      </Group>

      <Group title="Suggestions" about="Books members put forward for the club to read someday.">
        <div className="switch">
          <div><div className="k">Who can suggest{tick("onDeckPolicy")}</div><div className="d">Who can add a book to the club's suggestions. Everyone can still mark one Want to read.</div></div>
          <div className="seg" role="group" aria-label="Who can suggest">
            <button type="button" aria-pressed={s.onDeckPolicy === "everyone"} onClick={() => put("onDeckPolicy", { settings: { onDeckPolicy: "everyone" } })}>Everyone</button>
            <button type="button" aria-pressed={s.onDeckPolicy === "admins"} onClick={() => put("onDeckPolicy", { settings: { onDeckPolicy: "admins" } })}>Admins</button>
          </div>
        </div>
        <hr className="divider" />
        <div className="switch">
          <div><div className="k">Limit per member{tick("suggestionCap")}</div><div className="d">Cap how many suggestions each member can have at once, so a few people can't fill the list. When off, there's no limit.</div></div>
          <span className="row" style={{ gap: 10, flex: "none" }}>
            {s.suggestionCap !== null && <NumberSetting id="s-cap" label="Limit per member" value={s.suggestionCap} min={1} max={50} unit="each" onSave={(v) => put("suggestionCap", { settings: { suggestionCap: v ?? 3 } })} />}
            <input id="s-cap-on" className="toggle" type="checkbox" checked={s.suggestionCap !== null} aria-label="Limit suggestions per member"
              onChange={(e) => put("suggestionCap", { settings: { suggestionCap: e.target.checked ? 3 : null } })} />
          </span>
        </div>
        <hr className="divider" />
        <div className="switch">
          <div><div className="k">Tidy away old suggestions{tick("retireQuiet")}</div><div className="d">Suggestions nobody has shown new interest in for a while move to a Past list, out of the way. Nothing is deleted: anyone can bring one back by marking it Want to read, and whoever suggested a book hears two weeks before it moves.</div></div>
          <input id="s-retire" className="toggle" type="checkbox" checked={s.retireQuiet} aria-label="Tidy away old suggestions"
            onChange={(e) => put("retireQuiet", { settings: { retireQuiet: e.target.checked } })} />
        </div>
        {s.retireQuiet && (
          <div className="sub-settings">
            <div className="switch">
              <div><div className="k">Always keep the top{tick("retireKeep")}</div><div className="d">most wanted, however old</div></div>
              <NumberSetting id="s-keep" label="Always keep the top" value={s.retireKeep} min={1} max={200} unit="books" onSave={(v) => put("retireKeep", { settings: { retireKeep: v ?? 12 } })} />
            </div>
            <div className="switch">
              <div><div className="k">Tidy the rest after{tick("retireDays")}</div><div className="d">with nobody new wanting to read it</div></div>
              <NumberSetting id="s-days" label="Tidy the rest after" value={s.retireDays} min={14} max={730} unit="days" onSave={(v) => put("retireDays", { settings: { retireDays: v ?? 90 } })} />
            </div>
          </div>
        )}
      </Group>
      {save.isError && <p className="error">{describeError(save.error)}</p>}
    </>
  );
}

/** Who picks next, and the order after them. Admins can skip whoever is up. */
function TurnOrder({ clubId }: { clubId: string }) {
  const qc = useQueryClient();
  const books = useQuery({ queryKey: ["books", clubId], queryFn: () => api.clubBooks(clubId) });
  const skip = useMutation({ mutationFn: () => api.skipTurn(clubId), onSuccess: () => qc.invalidateQueries({ queryKey: ["books", clubId] }) });
  const t = books.data?.turn;
  if (!t) return null;
  const i = t.order.findIndex((m) => m.id === t.user.id);
  const order = [...t.order.slice(i), ...t.order.slice(0, i)];
  return (
    <div className="turns">
      {order.map((m, n) => (
        <div key={m.id} className="turn">
          <span className="num">{n + 1}</span><Avatar name={m.name} image={m.image} /><span>{m.name}</span>
          {n === 0 && <span className="n now">next</span>}
          {n === 0 && <button type="button" className="linkish small" disabled={skip.isPending} onClick={() => skip.mutate()}>skip</button>}
        </div>
      ))}
      {skip.isError && <p className="error small">{describeError(skip.error)}</p>}
    </div>
  );
}

/**
 * A typed setting shown as text with a pencil. The pencil turns it into an
 * input with Save and Cancel; nothing changes until Save. Enter saves a
 * one-line field (Ctrl/⌘+Enter a long one), Escape cancels.
 */
function EditField({ id, label, value, max, multiline, required, placeholder, hint, saved, onSave }:
  { id: string; label: string; value: string; max: number; multiline?: boolean; required?: boolean; placeholder?: string; hint?: string; saved?: React.ReactNode; onSave: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [more, setMore] = useState(false);
  const [v, setV] = useState(value);
  useEffect(() => { if (!editing) setV(value); }, [value, editing]);
  const t = v.trim();
  const can = t !== value.trim() && (!required || t.length > 0);
  const commit = () => { if (can) onSave(t); setEditing(false); };
  const cancel = () => { setV(value); setEditing(false); };
  const keys = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); cancel(); }
    if (e.key === "Enter" && (!multiline || e.metaKey || e.ctrlKey)) { e.preventDefault(); commit(); }
  };
  if (!editing) {
    return (
      <div className="field efield">
        <label htmlFor={`${id}-edit`}>{label}{saved}</label>
        <div className="eview">
          <span className="ev-text-wrap">
            <span className={`ev-text${multiline ? " multi" : ""}${multiline && !more ? " clamp" : ""}${value ? "" : " empty"}`}>{value || placeholder || "Not set"}</span>
            {multiline && !more && value.length > 160 && <button type="button" className="linkish small" onClick={() => setMore(true)}>more</button>}
          </span>
          <button id={`${id}-edit`} type="button" className="pencil" aria-label={`Edit ${label.toLowerCase()}`} onClick={() => setEditing(true)}><PencilIcon width={16} height={16} /></button>
        </div>
        {hint && <span className="muted small">{hint}</span>}
      </div>
    );
  }
  return (
    <div className="field efield">
      <label htmlFor={id}>{label}</label>
      {multiline
        ? <textarea id={id} className="input" value={v} maxLength={max} rows={4} placeholder={placeholder} autoFocus onChange={(e) => setV(e.target.value)} onKeyDown={keys} />
        : <input id={id} className="input" value={v} maxLength={max} placeholder={placeholder} autoFocus onChange={(e) => setV(e.target.value)} onKeyDown={keys} />}
      <div className="row" style={{ gap: 8 }}>
        <button type="button" className="btn primary sm" disabled={!can} onClick={commit}>Save</button>
        <button type="button" className="btn ghost sm" onClick={cancel}>Cancel</button>
        {multiline && <span className="muted small" style={{ marginLeft: "auto" }}>{max - v.length}</span>}
      </div>
      {hint && <span className="muted small">{hint}</span>}
    </div>
  );
}

export const VIS_OPTIONS: { key: Visibility; label: string; line: string }[] = [
  { key: "private", label: "Private", line: "Only people with an invite link can join. The club doesn't appear anywhere." },
  { key: "request", label: "Request to join", line: "Listed in the Directory. People ask to join and an admin decides." },
  { key: "open", label: "Open", line: "Listed in the Directory. Anyone signed in can join straight away." },
];

/** The three modes as a radio list (also used by the creation flow). `disabled` greys the public ones. */
export function VisibilityChoice({ value, onChange, disabled, readOnly }: { value: Visibility; onChange: (v: Visibility) => void; disabled?: boolean; readOnly?: boolean }) {
  return (
    <div className="vis-list" role="radiogroup" aria-label="Who can find this club">
      {VIS_OPTIONS.map((o) => {
        const off = readOnly ? o.key !== value : disabled && o.key !== "private";
        return (
          <button key={o.key} type="button" role="radio" aria-checked={value === o.key} className={`vis-opt${value === o.key ? " on" : ""}${off ? " off" : ""}`}
            disabled={off || readOnly} onClick={() => onChange(o.key)}>
            <span className="radio-dot" aria-hidden="true" /><span><b>{o.label}</b><span className="d">{o.line}</span></span>
          </button>
        );
      })}
    </div>
  );
}

/** Settings: the owner picks the mode; each change that opens or closes the club asks first. */
function VisibilitySetting({ club, saved, onSaved }: { club: Club; saved: React.ReactNode; onSaved: () => void }) {
  const qc = useQueryClient();
  const [asking, setAsking] = useState<Visibility | null>(null);
  const owner = club.myRole === "owner";
  const noDesc = !club.description.trim();
  const waiting = club.pendingRequests ?? 0;
  const set = useMutation({
    mutationFn: (v: Visibility) => api.setVisibility(club.id, v),
    onSuccess: async () => { setAsking(null); await Promise.all([qc.invalidateQueries({ queryKey: ["club"] }), qc.invalidateQueries({ queryKey: ["join-requests", club.id] }), qc.invalidateQueries({ queryKey: ["members", club.id] })]); onSaved(); },
  });
  const choose = (v: Visibility) => {
    if (v === club.visibility) return;
    if (club.visibility === "open" && v === "request") { set.mutate(v); return; } // no effect on anyone: no question
    setAsking(v);
  };
  return (
    <>
      <div className="field"><label>Mode{saved}</label>
        <VisibilityChoice value={club.visibility} onChange={choose} disabled={noDesc} readOnly={!owner} /></div>
      {!owner ? <p className="muted small" style={{ margin: 0 }}>Only the owner can change this.</p>
        : noDesc && club.visibility === "private" ? <p className="small notice" style={{ margin: 0 }}>Add a one-line description under The club first: it's what people see in the Directory.</p>
        : <p className="muted small" style={{ margin: 0 }}>Invite links work in every mode.</p>}
      {asking && (
        <div className="vis-confirm card" role="alertdialog" aria-label="Confirm">
          {asking === "private" ? (
            <>
              <b>Make {club.name} private?</b>
              <p className="muted small">It leaves the Directory straight away, and its page shows as private.{waiting > 0 ? ` The ${waiting} ${waiting === 1 ? "person" : "people"} waiting for an answer will be declined.` : ""} Members stay.</p>
            </>
          ) : club.visibility === "request" && asking === "open" ? (
            <>
              <b>Switch to Open?</b>
              <p className="muted small">Anyone signed in will be able to join straight away.{waiting > 0 ? <> The <b>{waiting} {waiting === 1 ? "person" : "people"}</b> waiting for an answer will be let in now.</> : ""}</p>
            </>
          ) : (
            <>
              <b>{asking === "open" ? `Make ${club.name} open?` : `List ${club.name} in the Directory?`}</b>
              <p className="muted small">{asking === "open" ? "It appears in the Directory right away, and anyone signed in can join." : "It appears in the Directory right away, and people can ask to join."} People who aren't members will see:</p>
              <ul className="allow">
                <li>Name, description and how the club works</li>
                <li>The current book and the last few on the shelf</li>
                <li>Usual pace, how the next book is picked, and how often you meet</li>
                <li>Genres, when the club started, and how many members</li>
                <li>The owner's and admins' names and photos</li>
              </ul>
              <p className="muted small">Never shown: Talk, the noticeboard, anyone's progress, meeting place and date, suggestions, ratings, or the member list.</p>
            </>
          )}
          {set.isError && <p className="error small">{describeError(set.error)}</p>}
          <div className="row" style={{ gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="btn ghost" onClick={() => setAsking(null)}>Cancel</button>
            <button type="button" className="btn primary" disabled={set.isPending} onClick={() => set.mutate(asking)}>
              {asking === "private" ? "Make it private" : club.visibility === "request" && asking === "open" ? (waiting > 0 ? `Switch and let ${waiting} in` : "Switch to Open") : asking === "open" ? "Make it open" : "List it"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** "✓ Saved", popping in beside a setting's name for a moment after it saves. */
export function Saved({ on }: { on: boolean }) {
  return <span className={`saved-tick${on ? " on" : ""}`} role="status" aria-live="polite">{on && <><span className="pop-check" aria-hidden="true">✓</span>Saved</>}</span>;
}

/** The club's link. Edited and saved on its own; the old link stops working. */
function SlugField({ club, onSave, saved }: { club: Club; onSave: (v: string) => void; saved?: React.ReactNode }) {
  const navigate = useNavigate();
  const [v, setV] = useState(club.slug);
  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => { setV(club.slug); }, [club.slug]);
  const origin = window.location.origin.replace(/^https?:\/\//, "");
  if (!editing) {
    return (
      <div className="field"><label>Link{saved}</label>
        <div className="row small" style={{ gap: 10, flexWrap: "wrap" }}>
          <span className="muted">{origin}/clubs/</span><b>{club.slug}</b>
          <button type="button" className="pencil" aria-label="Change the link" onClick={() => setEditing(true)}><PencilIcon width={16} height={16} /></button>
          <button type="button" className={`linkish small${copied ? " copied" : " muted"}`} aria-live="polite"
            onClick={() => { void navigator.clipboard?.writeText(`${window.location.origin}/clubs/${club.slug}`).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => undefined); }}>
            {copied ? <><span className="pop-check" aria-hidden="true">✓</span>copied</> : "copy"}</button>
        </div>
      </div>
    );
  }
  return (
    <div className="field"><label htmlFor="s-slug">Link</label>
      <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
        <span className="muted small">{origin}/clubs/</span>
        <input id="s-slug" className="input" style={{ maxWidth: 220 }} value={v} maxLength={40} autoFocus
          onChange={(e) => setV(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (v !== club.slug) onSave(v); setEditing(false); } if (e.key === "Escape") setEditing(false); }} />
        <button type="button" className="btn primary sm" disabled={v === club.slug || v.length < 3} onClick={() => { onSave(v); setEditing(false); void navigate({ to: "/clubs/$clubId/club/settings", params: { clubId: v }, replace: true }); }}>Save</button>
        <button type="button" className="btn ghost sm" onClick={() => { setV(club.slug); setEditing(false); }}>Cancel</button>
      </div>
      <p className="muted small">Lowercase letters, digits and hyphens. The old link stops working once you change it.</p>
      {club.visibility !== "private" && <p className="small notice">The club is public, so links people have shared will stop working.</p>}
    </div>
  );
}

/** Pages a day, saved on blur or Enter; blank clears it. */
function Membership({ club, members, meId }: { club: Club; members: Member[]; meId?: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [heir, setHeir] = useState("");
  const leaveClub = () => { qc.clear(); void navigate({ to: "/clubs", replace: true }); };
  const leave = useMutation({ mutationFn: () => api.removeMember(club.id, meId!), onSuccess: leaveClub });
  const del = useMutation({ mutationFn: () => api.deleteClub(club.id), onSuccess: leaveClub });
  const transfer = useMutation({
    mutationFn: () => api.transferClub(club.id, heir),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["club"] }), qc.invalidateQueries({ queryKey: ["members"] }), qc.invalidateQueries({ queryKey: ["me"] })]),
  });
  const others = members.filter((m) => m.id !== meId);

  const owner = club.myRole === "owner";
  return (
    <Group title="Your membership" about={owner ? "You own this club. Hand it on before you leave." : "Leaving keeps your comments and progress."}>
      <MuteClub clubId={club.id} />
      <hr className="divider" />
      {owner ? (
        <>
          <div><div className="k" style={{ fontWeight: 500 }}>Transfer ownership</div><div className="muted small">You stay on as an admin. Required before you can leave.</div></div>
          <div className="row">
            <select id="transfer-heir" className="input" value={heir} onChange={(e) => setHeir(e.target.value)} aria-label="New owner" disabled={others.length === 0}>
              <option value="">{others.length ? "Choose a member" : "No other members yet"}</option>
              {others.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <button className="btn" disabled={!heir || transfer.isPending} onClick={() => transfer.mutate()}>Transfer</button>
          </div>
          {transfer.isError && <p className="error">{describeError(transfer.error)}</p>}
          <hr className="divider" />
          <Confirm label="Delete this club" busy={del.isPending} onConfirm={() => del.mutate()}
            question={`Delete ${club.name} for everyone? Books, progress and the whole discussion go with it. There is no undo.`} />
          {del.isError && <p className="error">{describeError(del.error)}</p>}
        </>
      ) : (
        <>
          <Confirm label="Leave this club" busy={leave.isPending} onConfirm={() => leave.mutate()}
            question="Your comments and progress stay, marked as a former member. You'd need a new invite to come back." />
          {leave.isError && <p className="error">{describeError(leave.error)}</p>}
        </>
      )}
    </Group>
  );
}

/**
 * A number setting, the same way as the typed fields: the value as text with
 * a pencil; the pencil opens a small input with Save and Cancel. Out-of-range
 * numbers can't be saved, and the range shows under the input. `clearable`
 * lets it be left blank (Usual pace).
 */
function NumberSetting({ id, label, value, min, max, unit, clearable, onSave }:
  { id: string; label: string; value: number | null; min: number; max: number; unit: string; clearable?: boolean; onSave: (v: number | null) => void }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value === null ? "" : String(value));
  useEffect(() => { if (!editing) setV(value === null ? "" : String(value)); }, [value, editing]);
  const n = v.trim() === "" ? null : Number(v);
  const valid = n === null ? Boolean(clearable) : Number.isInteger(n) && n >= min && n <= max;
  const can = valid && n !== value;
  const commit = () => { if (can) onSave(n); setEditing(false); };
  const cancel = () => setEditing(false);
  if (!editing) {
    return (
      <span className="nview">
        <span className="nval">{value === null ? <span className="muted">Not set</span> : <><b className="num">{value}</b> <span className="muted">{unit}</span></>}</span>
        <button type="button" className="pencil" aria-label={`Edit ${label.toLowerCase()}`} onClick={() => setEditing(true)}><PencilIcon width={16} height={16} /></button>
      </span>
    );
  }
  return (
    <span className="nedit">
      <span className="row" style={{ gap: 6 }}>
        <input id={id} className="input num" type="number" inputMode="numeric" min={min} max={max} value={v} autoFocus aria-label={`${label}, ${unit}`} placeholder={clearable ? "—" : undefined}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") { e.preventDefault(); cancel(); } }} />
        <span className="muted small" style={{ whiteSpace: "nowrap" }}>{unit}</span>
        <button type="button" className="btn primary sm" disabled={!can} onClick={commit}>Save</button>
        <button type="button" className="btn ghost sm" onClick={cancel}>Cancel</button>
      </span>
      <span className={`small ${valid ? "muted" : "warn"}`}>{min}–{max}{clearable ? ", or blank" : ""}</span>
    </span>
  );
}
